
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Channel } from "@/lib/types";
import { toast } from "sonner";
import { handleError } from "@/utils/error-handler";
import { safeSelect, toUUID, isDatabaseRecord } from "@/utils/supabase";

export function useChannelsList(groupId: string) {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChannels();

    // Set up a realtime subscription for channel changes
    const channel = supabase
      .channel("channels-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "channels",
          filter: `group_id=eq.${groupId}`,
        },
        () => {
          fetchChannels();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [groupId]);

  const fetchChannels = async () => {
    try {
      setLoading(true);
      
      const query = supabase
        .from("channels")
        .select("*")
        .eq("group_id", toUUID(groupId) as any)
        .order("created_at", { ascending: true });

      const channelsData = await safeSelect(query, "Failed to load channels");

      if (channelsData && Array.isArray(channelsData)) {
        // Transform database data to our Channel type safely
        const formattedChannels: Channel[] = channelsData
          .filter(channel => isDatabaseRecord(channel))
          .map((channel: any) => ({
            id: channel.id,
            name: channel.name,
            description: channel.description || "",
            isAnnouncement: channel.is_announcement || false,
            isTemporary: channel.is_temporary || false,
            autoDeleteAfter: channel.auto_delete_after,
            groupId: channel.group_id,
            createdAt: channel.created_at,
            updatedAt: channel.updated_at,
            createdBy: channel.created_by,
          }));
        
        setChannels(formattedChannels);
      } else {
        setChannels([]);
      }
    } catch (error: any) {
      console.error("Error fetching channels:", error);
      toast.error(handleError(error, "Failed to load channels"));
      setChannels([]);
    } finally {
      setLoading(false);
    }
  };

  return {
    channels,
    loading,
    fetchChannels
  };
}
